import RBush from 'rbush';

import { getBalloonShapeOffsetX } from './getBalloonShapeOffsetX';
import { getBalloonZIndex } from './getBalloonZIndex';
import { getIsHighlighted } from './getIsHighlighted';
import { getPinViewModel, IPinViewModel } from './getPinViewModel';
import { getPricePillRenderer } from './markups/price/getPricePillRenderer';
import * as styles from './styles.css';
import { getPinAnchor } from '../../../constants/clusterPins/getPinAnchor';
import { EClusterPinAnchor } from '../../../constants/clusterPins/types';
import { IFeatureProperties, IRBushItem, TFeature } from '../../../types/map';

interface IBalloonManagerOptions {
  map: YMaps.Map;
  rbush: RBush<IRBushItem>;
}

/**
 * Кэши по строке контента балуна: при пересборке пинов (каждый зум-тик) сотни балунов
 * пересоздаются, но уникальных вариантов вёрстки немного (цена + флаги + anchor).
 * - layoutClassCache переиспользует классы templateLayoutFactory.createClass;
 * - shapeSizeCache хранит измеренный размер балуна — одинаковый контент имеет одинаковый
 *   размер, что убирает каскад forced reflow при добавлении оверлеев.
 * Лимит защищает от неограниченного роста за долгую сессию.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const layoutClassCache = new Map<string, any>();
const shapeSizeCache = new Map<string, { width: number; height: number }>();
const LAYOUT_CACHE_LIMIT = 500;

// Размер порции балунов, добавляемых в ObjectManager за один кадр
const BALLOONS_RENDER_CHUNK_SIZE = 100;

export function defineBalloonManager(ymaps: YMaps.IYMaps) {
  if (ymaps.modules.isDefined('BalloonManager')) {
    return;
  }

  ymaps.modules.define<
    [YMaps.ITemplateLayoutFactory, typeof YMaps.shape.Rectangle, typeof YMaps.geometry.pixel.Rectangle]
  >(
    'BalloonManager',
    ['templateLayoutFactory', 'shape.Rectangle', 'geometry.pixel.Rectangle'],
    (
      provide,
      templateLayoutFactory: YMaps.ITemplateLayoutFactory,
      shapeRectangle: typeof YMaps.shape.Rectangle,
      geometryPixelRectangle: typeof YMaps.geometry.pixel.Rectangle,
    ) => {
      class BalloonManager {
        public readonly objectManager: YMaps.ObjectManager;

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        private map: YMaps.Map;
        private rbush: RBush<IRBushItem>;
        private renderGeneration = 0;

        public constructor(options: IBalloonManagerOptions) {
          this.map = options.map;
          this.rbush = options.rbush;
          this.objectManager = new ymaps.ObjectManager();
        }

        public updateBalloons = (maxPinsPerCluster: number) => {
          // В менеджер загружаются ВСЕ раскрытые пины (не только видимые):
          // ObjectManager сам виртуализирует рендер по вьюпорту, поэтому
          // панорамирование на том же зуме не требует пересборки вовсе
          const rBushItems = this.rbush.all();

          // generation отменяет недорисованные предыдущие циклы при быстрых повторных обновлениях
          this.renderGeneration += 1;
          const generation = this.renderGeneration;

          // Вся перерисовка разнесена по кадрам:
          // - removeAll в своём кадре (удаление сотен DOM-узлов не блокирует обработчик зума);
          // - add отделён от removeAll кадром: на фичи с тем же id в одном тике
          //   ymaps не перерисовывает overlay — балун остаётся на старой геометрии;
          // - вставка чанками держит каждый кадр коротким на сотнях балунов.
          window.requestAnimationFrame(() => {
            if (generation !== this.renderGeneration) {
              return;
            }

            this.objectManager.removeAll();
            const features = rBushItems
              .map(item => this.prepareBalloon(item, maxPinsPerCluster))
              .filter(feature => !!feature) as TFeature[];

            let chunkStart = 0;
            const addChunk = () => {
              if (generation !== this.renderGeneration) {
                return;
              }

              this.objectManager.add({
                type: 'FeatureCollection',
                features: features.slice(chunkStart, chunkStart + BALLOONS_RENDER_CHUNK_SIZE),
              });
              chunkStart += BALLOONS_RENDER_CHUNK_SIZE;

              if (chunkStart < features.length) {
                window.requestAnimationFrame(addChunk);
              }
            };

            window.requestAnimationFrame(addChunk);
          });
        };

        public updateBalloonProperties = (
          featureId: string,
          properties: IFeatureProperties,
          maxPinsPerCluster: number,
        ) => {
          const model = getPinViewModel(properties);

          this.objectManager.objects.setObjectOptions(featureId, {
            iconLayout: this.prepareBalloonLayout(properties, model, maxPinsPerCluster),
            zIndex: getBalloonZIndex(properties, model, maxPinsPerCluster),
          });
        };

        private prepareBalloon = ({ feature }: IRBushItem, maxPinsPerCluster: number) => {
          const { properties } = feature;
          const model = getPinViewModel(properties);

          if (model.price === null) {
            return null;
          }

          return {
            ...feature,
            options: {
              iconLayout: this.prepareBalloonLayout(properties, model, maxPinsPerCluster),
              zIndex: getBalloonZIndex(properties, model, maxPinsPerCluster),
            },
          };
        };

        private prepareBalloonLayout = (
          properties: IFeatureProperties,
          model: IPinViewModel,
          maxPinsPerCluster: number,
        ) => {
          // anchor слота — горизонтальная привязка балуна к гео-точке (одинаков для координаты,
          // CSS-трансформа [data-anchor] и формы getShape). Резолвится один раз и захватывается
          // в замыкание getShape, поэтому форма не зависит от чтения DOM.
          const anchor = getPinAnchor(properties, maxPinsPerCluster);
          const content = this.prepareBalloonContent(properties, model, anchor);
          const cachedLayoutClass = layoutClassCache.get(content);

          if (cachedLayoutClass) {
            return cachedLayoutClass;
          }

          const layoutClass = templateLayoutFactory.createClass(content, {
            getShape() {
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              const element: HTMLElement = (this as any).getElement();
              if (!element) {
                return null;
              }

              const pinElement = element.firstElementChild;
              if (!pinElement) {
                return null;
              }

              // размер балуна определяется только его контентом — кэш убирает
              // forced reflow (getBoundingClientRect) на каждый добавленный балун
              let size = shapeSizeCache.get(content);
              if (!size) {
                const boundingRect = pinElement.getBoundingClientRect();
                if (!boundingRect.width) {
                  return null;
                }
                size = { width: boundingRect.width, height: boundingRect.height };
                shapeSizeCache.set(content, size);
              }

              // горизонталь по anchor (left/center/right), верх балуна — ровно на гео-точке (offsetY=0)
              const offsetX = getBalloonShapeOffsetX(anchor, size.width);
              const offsetY = 0;

              return new shapeRectangle(
                new geometryPixelRectangle([
                  [offsetX, offsetY],
                  [offsetX + size.width, offsetY + size.height],
                ]),
              );
            },
          });

          if (layoutClassCache.size > LAYOUT_CACHE_LIMIT) {
            layoutClassCache.clear();
            shapeSizeCache.clear();
          }
          layoutClassCache.set(content, layoutClass);

          return layoutClass;
        };

        private prepareBalloonContent(feature: IFeatureProperties, model: IPinViewModel, anchor: EClusterPinAnchor) {
          const { isActive, offers } = feature;
          const { testData } = offers[0] || {};
          const testDataAttributes = testData
            ? Object.entries(testData)
                .map(([name, value]) => `${name}="${value}"`)
                .join(' ')
            : '';

          const isHighlighted = getIsHighlighted(feature);
          const { isViewed, isFavorite, isMultiPin } = model;

          // Обёртка .pin — привязка к гео-точке и data-атрибуты для тестов и превью
          // (превью меряет ширину выбранного пина по data-is-selected). Тело пина рисует markups/*.
          return `
            <div
              class="${styles['pin']}"
              data-testid="pin"
              data-anchor="${anchor}"
              data-is-active="${Boolean(isHighlighted)}"
              data-is-selected="${Boolean(isActive)}"
              data-is-viewed="${Boolean(isViewed)}"
              data-is-favorite="${Boolean(isFavorite)}"
              data-is-multi="${isMultiPin}"
              ${testDataAttributes}
            >
              ${getPricePillRenderer(model, { isHighlighted })}
            </div>
          `;
        }
      }

      provide(BalloonManager);
    },
  );
}
